import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Package as PackageIcon, Download } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadCsv, getList } from "@/lib/api";
import Modal, { inputCls, btnPrimary, btnSecondary, Field } from "@/components/Modal";
import Pager from "@/components/Pager";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { inr, label, CHANNELS, PAGE_SIZE } from "@/lib/constants";

function VariantForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial
      ? { ...initial, componentsText: initial.components.map((c) => `${c.name}: ${c.cost}`).join("\n") }
      : { name: "", description: "", product_category: "", componentsText: "", notes: "" }
  );
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const components = form.componentsText
      .split("\n")
      .map((line) => {
        const [name, cost] = line.split(":").map((s) => s?.trim());
        return name ? { name, cost: Number(cost) || 0 } : null;
      })
      .filter(Boolean);
    const payload = { name: form.name, description: form.description, product_category: form.product_category, components, notes: form.notes };
    try {
      if (initial?.id) {
        await api.put(`/packaging-variants/${initial.id}`, payload);
        toast.success("Variant updated");
      } else {
        await api.post("/packaging-variants", payload);
        toast.success("Variant created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Modal title={initial?.id ? "Edit Packaging Variant" : "New Packaging Variant"} onClose={onClose} testid="variant-form-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="variant-form">
        <Field label="Name">
          <input data-testid="variant-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
        </Field>
        <Field label="Product category">
          <input data-testid="variant-category-input" value={form.product_category} onChange={set("product_category")} className={inputCls} placeholder="e.g. bars, truffles" />
        </Field>
        <Field label="Description">
          <textarea data-testid="variant-description-input" rows={2} value={form.description} onChange={set("description")} className={inputCls} />
        </Field>
        <Field label="Components (one per line: name: cost)">
          <textarea data-testid="variant-components-input" rows={3} value={form.componentsText} onChange={set("componentsText")} className={`${inputCls} font-mono text-xs`} placeholder={"Rigid board box: 42\nGold foil stamping: 8"} />
        </Field>
        <Field label="Notes">
          <input data-testid="variant-notes-input" value={form.notes} onChange={set("notes")} className={inputCls} />
        </Field>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="variant-cancel-button">Cancel</button>
          <button type="submit" className={btnPrimary} data-testid="variant-save-button">Save variant</button>
        </div>
      </form>
    </Modal>
  );
}

function ScenarioForm({ initial, onClose, onSaved }) {
  const { data: formulas = [] } = useQuery({ queryKey: ["formulas-all"], queryFn: async () => (await api.get("/formulas", { params: { limit: 500 } })).data });
  const { data: variants = [] } = useQuery({ queryKey: ["variants"], queryFn: async () => (await api.get("/packaging-variants")).data });
  const [form, setForm] = useState(
    initial || { formula_id: "", packaging_variant_id: "", ingredient_cost_per_unit: "", packaging_cost_per_unit: "", proposed_retail_price: "", channel: "retail_store", notes: "" }
  );
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      formula_id: form.formula_id,
      packaging_variant_id: form.packaging_variant_id || null,
      ingredient_cost_per_unit: Number(form.ingredient_cost_per_unit) || 0,
      packaging_cost_per_unit: Number(form.packaging_cost_per_unit) || 0,
      proposed_retail_price: Number(form.proposed_retail_price) || 0,
      channel: form.channel,
      notes: form.notes,
    };
    try {
      if (initial?.id) {
        await api.put(`/costing-scenarios/${initial.id}`, payload);
        toast.success("Scenario updated");
      } else {
        await api.post("/costing-scenarios", payload);
        toast.success("Scenario created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Modal title={initial?.id ? "Edit Costing Scenario" : "New Costing Scenario"} onClose={onClose} testid="scenario-form-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="scenario-form">
        <Field label="Formula">
          <select data-testid="scenario-formula-select" required value={form.formula_id} onChange={set("formula_id")} className={inputCls}>
            <option value="">Select formula…</option>
            {formulas.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </Field>
        <Field label="Packaging variant">
          <select data-testid="scenario-packaging-select" value={form.packaging_variant_id || ""} onChange={set("packaging_variant_id")} className={inputCls}>
            <option value="">No packaging</option>
            {variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Ingredient cost/unit">
            <input data-testid="scenario-ingredient-cost-input" type="number" step="0.5" min={0} required value={form.ingredient_cost_per_unit} onChange={set("ingredient_cost_per_unit")} className={inputCls} />
          </Field>
          <Field label="Packaging cost/unit">
            <input data-testid="scenario-packaging-cost-input" type="number" step="0.5" min={0} required value={form.packaging_cost_per_unit} onChange={set("packaging_cost_per_unit")} className={inputCls} />
          </Field>
          <Field label="Retail price">
            <input data-testid="scenario-price-input" type="number" step="5" min={0} required value={form.proposed_retail_price} onChange={set("proposed_retail_price")} className={inputCls} />
          </Field>
        </div>
        <Field label="Channel">
          <select data-testid="scenario-channel-select" value={form.channel} onChange={set("channel")} className={inputCls}>
            {CHANNELS.map((c) => <option key={c} value={c}>{label(c)}</option>)}
          </select>
        </Field>
        <Field label="Notes">
          <input data-testid="scenario-notes-input" value={form.notes} onChange={set("notes")} className={inputCls} />
        </Field>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="scenario-cancel-button">Cancel</button>
          <button type="submit" className={btnPrimary} data-testid="scenario-save-button">Save scenario</button>
        </div>
      </form>
    </Modal>
  );
}

export default function Packaging() {
  const { canWrite } = useAuth();
  const confirm = useConfirm();

  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null); // {type:'variant'|'scenario', initial?}
  const [channelFilter, setChannelFilter] = useState("");
  const [offset, setOffset] = useState(0);

  const { data: variants = [] } = useQuery({
    queryKey: ["variants"],
    queryFn: async () => (await api.get("/packaging-variants", { params: { limit: 200 } })).data,
  });

  const { data: scenarioPage, isLoading } = useQuery({
    queryKey: ["scenarios", channelFilter, offset],
    queryFn: () => getList("/costing-scenarios", { channel: channelFilter, limit: PAGE_SIZE, offset }),
  });

  const scenarios = scenarioPage?.items ?? [];

  const exportCsv = async () => {
    try {
      await downloadCsv("/costing-scenarios/export.csv", "smoor-costing-scenarios.csv");
      toast.success("Costing scenarios exported");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["variants"] });
    queryClient.invalidateQueries({ queryKey: ["scenarios"] });
  };

  const del = async (kind, id) => {
    const ok = await confirm({
      title: kind === "packaging-variants" ? "Delete packaging variant" : "Delete costing scenario",
      message: kind === "packaging-variants"
        ? "Delete this packaging variant? If any costing scenario uses it, the delete will be blocked."
        : "Delete this costing scenario? This cannot be undone.",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/${kind}/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const marginCls = (p) =>
    p >= 60 ? "text-[#15803D] bg-[#F0FDF4]" : p >= 45 ? "text-[#B45309] bg-[#FFFBEB]" : "text-[#B91C1C] bg-[#FEF2F2]";

  return (
    <div data-testid="packaging-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-4 border-b border-[#E8DFD1]">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1C100B]">Packaging & Costing</h1>
          <p className="text-sm text-[#8C7A6B] mt-1">Packaging variants and margin scenarios.</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <button onClick={() => setModal({ type: "variant" })} className={btnSecondary} data-testid="new-variant-button"><Plus size={15} /> Variant</button>
            <button onClick={() => setModal({ type: "scenario" })} className={btnPrimary} data-testid="new-scenario-button"><Plus size={15} /> Costing Scenario</button>
          </div>
        )}
      </div>

      <h2 className="font-display text-xl font-semibold text-[#1C100B] mb-4">Packaging Variants</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10" data-testid="variants-grid">
        {variants.map((v) => {
          const total = (v.components || []).reduce((s, c) => s + (c.cost || 0), 0);
          return (
            <div key={v.id} className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm hover:border-[#C5A059] transition-colors" data-testid={`variant-card-${v.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <PackageIcon size={17} className="text-[#C5A059] shrink-0" />
                  <h3 className="font-semibold text-[#2A1810] text-sm">{v.name}</h3>
                </div>
                {canWrite && (
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setModal({ type: "variant", initial: v })} className="p-1.5 rounded hover:bg-[#FAF6F0] text-[#8C7A6B]" data-testid={`edit-variant-${v.id}`}><Pencil size={13} /></button>
                    <button onClick={() => del("packaging-variants", v.id)} className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#B91C1C]" data-testid={`delete-variant-${v.id}`}><Trash2 size={13} /></button>
                  </div>
                )}
              </div>
              <p className="text-xs text-[#8C7A6B] leading-relaxed mb-3 line-clamp-2">{v.description}</p>
              <div className="space-y-1.5 mb-3">
                {(v.components || []).map((c) => (
                  <div key={c.name} className="flex justify-between text-xs">
                    <span className="text-[#5C4A3E]">{c.name}</span>
                    <span className="text-[#2A1810] font-medium">{inr(c.cost)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-[#F4EFEA]">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#8C7A6B]">{label(v.product_category)}</span>
                <span className="text-sm font-bold text-[#2A1810]" data-testid={`variant-total-${v.id}`}>{inr(total)}/unit</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="font-display text-xl font-semibold text-[#1C100B]">Costing Scenarios</h2>
        <div className="flex gap-2 items-center">
          <button onClick={exportCsv} className={btnSecondary} data-testid="export-costing-button">
            <Download size={15} className="inline mr-1.5 -mt-0.5" /> Export CSV
          </button>
          <select data-testid="channel-filter" value={channelFilter} onChange={(e) => { setOffset(0); setChannelFilter(e.target.value); }} className={`${inputCls} w-52`}>
            <option value="">All channels</option>
            {CHANNELS.map((c) => <option key={c} value={c}>{label(c)}</option>)}
          </select>
        </div>
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-[#E8DFD1] bg-white shadow-sm" data-testid="scenarios-table">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#FAF6F0] border-b border-[#E8DFD1] text-[#2A1810] text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3.5 font-semibold">Formula</th>
              <th className="px-4 py-3.5 font-semibold">Packaging</th>
              <th className="px-4 py-3.5 font-semibold">Channel</th>
              <th className="px-4 py-3.5 font-semibold text-right">Ing. cost</th>
              <th className="px-4 py-3.5 font-semibold text-right">Pkg cost</th>
              <th className="px-4 py-3.5 font-semibold text-right">Total</th>
              <th className="px-4 py-3.5 font-semibold text-right">Price</th>
              <th className="px-4 py-3.5 font-semibold text-right">Margin</th>
              {canWrite && <th className="px-4 py-3.5" />}
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#8C7A6B]">Loading…</td></tr>}
            {!isLoading && scenarios.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-[#8C7A6B]" data-testid="scenarios-empty">No costing scenarios.</td></tr>
            )}
            {scenarios.map((c) => (
              <tr key={c.id} className="border-b border-[#F4EFEA] hover:bg-[#FDFBF7]" data-testid={`scenario-row-${c.id}`}>
                <td className="px-4 py-3.5 font-medium text-[#2A1810]">{c.formula_name}</td>
                <td className="px-4 py-3.5 text-[#5C4A3E]">{c.packaging_name}</td>
                <td className="px-4 py-3.5 text-[#5C4A3E]">{label(c.channel)}</td>
                <td className="px-4 py-3.5 text-right text-[#5C4A3E]">{inr(c.ingredient_cost_per_unit)}</td>
                <td className="px-4 py-3.5 text-right text-[#5C4A3E]">{inr(c.packaging_cost_per_unit)}</td>
                <td className="px-4 py-3.5 text-right font-medium text-[#2A1810]">{inr(c.total_cost_per_unit)}</td>
                <td className="px-4 py-3.5 text-right font-medium text-[#2A1810]">{inr(c.proposed_retail_price)}</td>
                <td className="px-4 py-3.5 text-right">
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${marginCls(c.contribution_margin_percent)}`} data-testid={`scenario-margin-${c.id}`}>
                    {c.contribution_margin_percent}%
                  </span>
                </td>
                {canWrite && (
                  <td className="px-4 py-3.5 text-right whitespace-nowrap">
                    <button onClick={() => setModal({ type: "scenario", initial: c })} className="p-1.5 rounded hover:bg-[#FAF6F0] text-[#8C7A6B]" data-testid={`edit-scenario-${c.id}`}><Pencil size={13} /></button>
                    <button onClick={() => del("costing-scenarios", c.id)} className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#B91C1C]" data-testid={`delete-scenario-${c.id}`}><Trash2 size={13} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <Pager offset={offset} limit={PAGE_SIZE} total={scenarioPage?.total ?? 0} onChange={setOffset} testid="scenarios-pager" />
      </div>

      {modal?.type === "variant" && <VariantForm initial={modal.initial} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal?.type === "scenario" && <ScenarioForm initial={modal.initial} onClose={() => setModal(null)} onSaved={refresh} />}
    </div>
  );
}
